import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PanelHostService } from '../../services/panel-host.service';
import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../services/auth.service';
import { CustomerServiceMessagesService } from '../../services/customer-service-messages.service';
import { SquawksService } from '../../services/squawks.service';
import { IonPopover, IonRouterLinkWithHref, ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TripService } from '../../services/trip.service';
import { NavVisibilityService } from '../../services/nav-visibility.service';
import { showPublicTripLink as tripHasPublicLink } from '../../services/public-trip-link';
import { daysUntilTrip } from '../../utilities/trip-date-utils';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { AccessibilitySettingsComponent } from '../accessibility-settings/accessibility-settings.component';
import { ActingAsBannerComponent } from '../acting-as-banner/acting-as-banner.component';
import { ImpersonationBannerComponent } from '../impersonation-banner/impersonation-banner.component';
import { BRAND_ASSETS } from '../../brand/brand-assets';
import { MyInfoService } from '../../services/my-info.service';
import { SidebarScrollAffordanceComponent } from './sidebar-scroll-affordance/sidebar-scroll-affordance.component';
import {
  INITIAL_SCROLL_AFFORDANCE_STATE,
  nextScrollAffordanceState,
  SCROLL_AFFORDANCE_CHEVRON_HIT_PX,
  SCROLL_AFFORDANCE_PAGE_RATIO,
  ScrollAffordanceState,
} from '../../utilities/scroll-affordance';
import { NAV_ICONS, NavIcon, NavIconId } from '../../nav/nav-icons';

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    FaIconComponent,
    RouterLink,
    RouterLinkActive,
    IonRouterLinkWithHref,
    IonPopover,
    AccessibilitySettingsComponent,
    ActingAsBannerComponent,
    ImpersonationBannerComponent,
    TranslocoDirective,
    SidebarScrollAffordanceComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  readonly brandAssets = BRAND_ASSETS;
  readonly navIcons: Readonly<Record<NavIconId, NavIcon>> = NAV_ICONS;

  private readonly panelHostService = inject(PanelHostService);
  private readonly themeService = inject(ThemeService);
  private readonly tripService = inject(TripService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly navVisibility = inject(NavVisibilityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly squawksService = inject(SquawksService);
  private readonly customerServiceMessages = inject(CustomerServiceMessagesService);
  private readonly toastController = inject(ToastController);
  private readonly transloco = inject(TranslocoService);
  private readonly myInfoService = inject(MyInfoService);

  readonly tripResource = this.tripService.currentTrip;
  readonly nav = this.navVisibility;

  readonly authUser = this.myInfoService.authUser;
  readonly profilePhotoLoadFailed = signal(false);

  readonly showSidebarProfilePhoto = computed(() => {
    const photoUrl = this.authUser()?.profilePhotoUrl;
    return Boolean(photoUrl) && !this.profilePhotoLoadFailed();
  });
  readonly squawksUnreadCount = this.squawksService.unreadCount;
  readonly chatterUnreadCount = this.customerServiceMessages.unreadCount;

  readonly footerExpanded = signal(false);
  readonly isDarkMode = computed(() => this.themeService.darkMode());

  private readonly scrollRegion =
    viewChild<ElementRef<HTMLElement>>('scrollRegion');
  private readonly scrollBody =
    viewChild<ElementRef<HTMLElement>>('scrollBody');
  private readonly scrollPort =
    viewChild<ElementRef<HTMLElement>>('scrollPort');
  readonly scrollAffordance = signal<ScrollAffordanceState>(
    INITIAL_SCROLL_AFFORDANCE_STATE,
  );

  private resizeObserver: ResizeObserver | null = null;
  private boundScrollListener: (() => void) | null = null;

  readonly daysUntilTrip = computed(() => {
    const trip = this.tripResource.value();
    if (!trip) {
      return 0;
    }
    return daysUntilTrip(new Date(trip.tripDate));
  });

  readonly hasTripNav = computed(() => {
    const phase = this.nav.tripPhase();
    return phase !== null && phase !== 'noTrip';
  });

  readonly showLeaderToolsSection = computed(
    () =>
      this.nav.showGroupManagementNav() ||
      (this.nav.showTripFinancesNav() && this.nav.canAccessTripFinances()),
  );

  readonly showPublicTripLink = computed(() =>
    tripHasPublicLink(this.tripResource.value()),
  );

  constructor() {
    effect(() => {
      this.authUser()?.profilePhotoUrl;
      this.profilePhotoLoadFailed.set(false);
    });

    effect(() => {
      const scrollRegion = this.scrollRegion();
      if (!scrollRegion) {
        return;
      }

      untracked(() => {
        this.setupScrollAffordanceObservers();
        this.updateScrollAffordance();
      });
    });

    this.destroyRef.onDestroy(() => {
      this.teardownScrollAffordanceObservers();
    });
  }

  private setupScrollAffordanceObservers(): void {
    const scrollElement = this.scrollRegion()?.nativeElement;
    if (!scrollElement || this.boundScrollListener) {
      return;
    }

    this.boundScrollListener = () => {
      this.updateScrollAffordance();
    };
    scrollElement.addEventListener('scroll', this.boundScrollListener, {
      passive: true,
    });

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollAffordance();
    });

    const scrollBody = this.scrollBody()?.nativeElement;
    if (scrollBody) {
      this.resizeObserver.observe(scrollBody);
    }

    const scrollPort = this.scrollPort()?.nativeElement;
    if (scrollPort) {
      this.resizeObserver.observe(scrollPort);
    }
  }

  private teardownScrollAffordanceObservers(): void {
    const scrollElement = this.scrollRegion()?.nativeElement;
    if (scrollElement && this.boundScrollListener) {
      scrollElement.removeEventListener('scroll', this.boundScrollListener);
    }

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.boundScrollListener = null;
  }

  onScrollAffordancePageDown(): void {
    const scrollElement = this.scrollRegion()?.nativeElement;
    if (!scrollElement) {
      return;
    }

    const behavior = this.themeService.reducedMotion() ? 'auto' : 'smooth';

    scrollElement.scrollBy({
      top: scrollElement.clientHeight * SCROLL_AFFORDANCE_PAGE_RATIO,
      behavior,
    });
  }

  private updateScrollAffordance(): void {
    const scrollElement = this.scrollRegion()?.nativeElement;
    if (!scrollElement) {
      return;
    }

    const reservedBottom = this.scrollAffordance().showBottomHint
      ? SCROLL_AFFORDANCE_CHEVRON_HIT_PX
      : 0;

    const metrics = {
      scrollTop: scrollElement.scrollTop,
      scrollHeight: scrollElement.scrollHeight,
      clientHeight: scrollElement.clientHeight,
      reservedBottom,
    };

    this.scrollAffordance.update((previous) => {
      const next = nextScrollAffordanceState(previous, metrics);
      if (
        next.showTopScrim === previous.showTopScrim &&
        next.showBottomHint === previous.showBottomHint &&
        next.playBob === previous.playBob &&
        next.hasBobbed === previous.hasBobbed
      ) {
        return previous;
      }
      return next;
    });
  }

  onSidebarProfilePhotoError(): void {
    this.profilePhotoLoadFailed.set(true);
  }

  toggleFooter(): void {
    this.footerExpanded.update((expanded) => !expanded);
  }

  openLocationSwap(): void {
    this.panelHostService.open('location-swap');
  }

  openProfilePanel(): void {
    this.panelHostService.open('profile');
  }

  openSquawks(): void {
    this.panelHostService.open('squawks');
  }

  openChatter(): void {
    this.panelHostService.open('chatter');
  }

  openPaymentPanel(): void {
    this.panelHostService.open('payment');
  }

  openTripQr(): void {
    this.panelHostService.open('trip-qr');
  }

  toggleDarkMode(): void {
    void this.themeService.toggleDarkMode();
  }

  async logout(): Promise<void> {
    await this.authService.logout();
  }

  async copyTripUrl(): Promise<void> {
    const trip = this.tripResource.value();
    if (!trip) {
      return;
    }
    const url = `https://ppm.org/${trip.id}`;
    await navigator.clipboard.writeText(url);

    const toast = await this.toastController.create({
      message: this.transloco.translate(marker('sidebar.trip-url-copied')),
      duration: 2000,
      position: 'bottom',
      color: 'success',
    });

    await toast.present();
  }
}
